import { useEffect, useState } from "react";
import { Rss, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";

/** Private subscribe-able calendar link: sittings appear in Google / Outlook / Apple automatically. */
export const CalendarFeed = () => {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/calendar/feed").then(({ data }) => setToken(data.token)).catch(() => {}); }, []);
  if (!token) return null;

  const url = `${API}/calendar/feed/${token}.ics`;
  const webcal = url.replace(/^https?:/, "webcal:");
  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error("Copy failed — select and copy the link manually"); }
  };
  const rotate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/calendar/feed/rotate");
      setToken(data.token);
      toast.success("New link issued — the old one no longer works");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="border border-border bg-card p-5" data-testid="calendar-feed">
      <p className="flex items-center gap-2 label-caps"><Rss className="h-3.5 w-3.5" /> Subscribe in your calendar</p>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
        Add this private link once and every sitting posted on your matters appears in Google
        Calendar, Outlook or Apple Calendar automatically, with a reminder the day before.
      </p>
      <input readOnly value={url} data-testid="calendar-feed-url" onFocus={(e) => e.target.select()}
        className="mt-4 w-full h-9 px-3 font-mono text-[11px] bg-muted border border-border rounded-none" />
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => copy(url, "Link")} data-testid="calendar-feed-copy"
          className="inline-flex items-center h-9 px-3 bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-[11px] transition-colors duration-200">
          <Copy className="h-3.5 w-3.5 mr-2" /> Copy link
        </button>
        <a href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`} target="_blank" rel="noopener noreferrer"
          data-testid="calendar-feed-google"
          className="inline-flex items-center h-9 px-3 border border-border hover:bg-accent uppercase tracking-widest text-[11px] transition-colors duration-200">
          Google Calendar
        </a>
        <a href={webcal} data-testid="calendar-feed-webcal"
          className="inline-flex items-center h-9 px-3 border border-border hover:bg-accent uppercase tracking-widest text-[11px] transition-colors duration-200">
          Outlook / Apple
        </a>
        <button onClick={rotate} disabled={busy} data-testid="calendar-feed-rotate"
          className="inline-flex items-center h-9 px-3 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
          <RefreshCw className="h-3.5 w-3.5 mr-2" /> {busy ? "Issuing…" : "Reset link"}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        Anyone with this link can read your sitting dates. Reset it if it is ever shared by mistake.
      </p>
    </section>
  );
};
